import React, { createContext, useContext, useState, ReactNode, useRef, MutableRefObject, useEffect } from "react"
import * as osfiles from "./osfiles"
import { fetchWithToken } from "@/common/fetch"
import { Translates } from "@/services/repository/i18n"

import './gallery.css'
import { Stream } from "@/services/common/stream"

const GalleryContext = createContext<GalleryContextType | undefined>(undefined)

export interface Image {
    id: string
    url: string
}

export type URLCB = (is: string) => string

export interface GalleryContextType {
    images: Image[] | undefined
    // setImages: (images: Image[]) => void
    // selectFile: (event: React.ChangeEvent<HTMLInputElement>) => void
    capturePhoto: () => Promise<void>
    selectFiles: () => Promise<void>
    removeFile: (fileId: string) => Promise<void>
    isPhotoCaptureSupported: boolean
    // uploadStatus: osfiles.UploadStatus | undefined
    statusStream: Stream<osfiles.UploadStatus>
    galleryId: MutableRefObject<string | undefined>
    isRefreshing: boolean
}

export interface GalleryProviderProps {
    children?: ReactNode
    insertUrl: string
    updateUrl: string
    fetchUrl: string
    removeUrl: string
    token?: string
    translates: Translates
    galleryId?: string
    onChange?: (id: string) => void
    setUploading?: (status: boolean) => void
    statusStream: Stream<osfiles.UploadStatus>
}


export const GalleryProvider: React.FC<GalleryProviderProps> = (props) => {
    const { children, insertUrl, fetchUrl, removeUrl, translates, updateUrl, token, onChange, setUploading } = props
    const [images, setImages] = useState<Image[] | undefined>(undefined)
    const galleryId = useRef<string | undefined>(props.galleryId)
    const [isRefreshing, setRefreshing] = useState<boolean>(false)
    const statusStreamRef = useRef<Stream<osfiles.UploadStatus>>(props.statusStream || new Stream<osfiles.UploadStatus>())

    const getGalleryURL = () => {
        if (galleryId.current === undefined) {
            return insertUrl
        }
        return updateUrl.replace('{id}', galleryId.current || '')
    }

    const refresh = async () => {
        if (galleryId.current === undefined) {
            setImages([])
            return
        }
        setRefreshing(true)
        const url = fetchUrl.replace('{id}', galleryId.current)
        return fetchWithToken(url, {
            token: token
        }).then(async (resp) => {
            if (!resp.ok) {
                console.error(`${url} fetch data error`)
                return
            }
            const data = await resp.json()
            setImages(data)
            if (onChange && galleryId.current) {
                onChange(galleryId.current)
            }
        }).then(() => setRefreshing(false), () => setRefreshing(false))
    }

    const afterUpload = (data: osfiles.CreateGalleryResponse | undefined) => {
        if (galleryId.current === undefined) {
            if (data?.id) {
                galleryId.current = data.id
            } else {
                throw new Error('Expected id returned by response when create gallery')
            }
        }
        if (setUploading) {
            setUploading(false)
        }
        refresh()
    }

    const selectFiles = async () => {
        osfiles.selectFiles().then(async (files) => {
            if (files === null || files.length === 0) {
                return
            }
            if (setUploading) {
                setUploading(true)
            }
            return osfiles.uploadFiles({
                url: getGalleryURL(),
                urlToken: token,
                files: files,
                onSuccess: afterUpload,
                onFail: () => setUploading && setUploading(false),
                translates,
                statusStream: statusStreamRef.current,
            })
        })
    }

    const capturePhoto = async () => {
        osfiles.takePhoto().then(async (files) => {
            if (files === null || files.length === 0) {
                return
            }
            if (setUploading) {
                setUploading(true)
            }
            return osfiles.uploadFiles({
                url: getGalleryURL(),
                urlToken: token,
                files: files,
                onSuccess: afterUpload,
                onFail: () => setUploading && setUploading(false),
                translates,
                statusStream: statusStreamRef.current,
            })
        })
    }

    const removeFile = async (fileId: string): Promise<void> => {
        const url = removeUrl.replace('{id}', galleryId.current || '').replace('{fid}', fileId)
        const headers = new Headers();
        if (token) {
            headers.set('Authorization', `Bearer ${token}`);
        }
        return fetch(url, {
            method: 'DELETE',
            headers,
        }).then(resp => {
            refresh()
            return
        });
    }

    const isPhotoCaptureSupported = osfiles.isPhotoCaptureSupported()

    useEffect(() => {
        if (galleryId === undefined) {
            return
        }
        refresh()
    }, [])

    return (
        <GalleryContext.Provider value={{ images, isPhotoCaptureSupported, capturePhoto, selectFiles, statusStream: statusStreamRef.current, galleryId, isRefreshing, removeFile }}>
            {children}
        </GalleryContext.Provider>
    )
}

export const useGallery = (): GalleryContextType => {
    const context = useContext(GalleryContext)
    if (!context) {
        throw new Error("useGallery must be used within a GalleryProvider")
    }
    return context
}

export const SimpleGallery = (props: { readOnly?: boolean, className?: string }) => {
    return <div className={`gallery-container ${props.className || 'simple-gallery-container'}`}>
        <SimpleGalleryImages>
            {props.readOnly ? null : <UploadButton />}
            {props.readOnly ? null : <CaptureButton />}
        </SimpleGalleryImages>
        <StatusBar />
    </div>
}

// export const SimpleGalleryWrapper = (props: GalleryProviderProps & { className?: string }) => <GalleryProvider {...props}>
//     <div className={`gallery-container ${props.className || 'simple-gallery-container'}`}>
//         {props.children}
//     </div>
// </GalleryProvider>

const ImageWithRemove = ({src, remove} : {src: string, remove: () => void}) => {
    const [showRemove, setShowRemove] = useState(false);
    const containerRef = useRef(null);

    const handleClickOutside = (event) => {
        if (containerRef.current && !containerRef.current.contains(event.target)) {
            setShowRemove(false)
        }
    }

    useEffect(() => {
        document.addEventListener("mousedown", handleClickOutside)
        return () => {
            document.removeEventListener("mousedown", handleClickOutside)
        }
    }, [])

    return <div ref={containerRef} onClick={() => setShowRemove(true)} className='image-row'>
        <img src={src} />
        {showRemove && (
            <div className="remove-box" onClick={remove}>
                <i className='svgfont-trash' />
            </div>
        )}
    </div>
}

export const SimpleGalleryImages = ({ children }: { children: ReactNode }) => {
    const { images, statusStream, isRefreshing, removeFile } = useGallery()
    const [status, setStatus] = useState<osfiles.UploadStatus>()

    useEffect(() => {
        const id = statusStream.listen((status) => {
            setStatus(status)
        })
        return () => {
            statusStream.remove(id)
        }
    })

    if (isRefreshing || status?.started) {
        return <div className="spinner"></div>
    }
    return <div className="gallery-images">
        {images ? images.map((row, i) => <ImageWithRemove key={i} src={row.url} remove={() => {
            removeFile(row.id)
        }}/>) : null}
        {children}
    </div>
}

export const UploadButton = () => {
    const { selectFiles } = useGallery()
    return <div className="gallery-button" onClick={() => selectFiles()}>
        <i className='svgfont-gallery-upload' />
    </div>
}

export const CaptureButton = () => {
    const { capturePhoto, isPhotoCaptureSupported } = useGallery()
    if (!isPhotoCaptureSupported) {
        return null
    }
    return <div className="gallery-button" onClick={() => capturePhoto()}>
        <i className='svgfont-gallery-camera' />
    </div>
}

export const StatusBar = () => {
    const { statusStream } = useGallery()
    const [status, setStatus] = useState<osfiles.UploadStatus>()

    useEffect(() => {
        const id = statusStream.listen((status) => {
            setStatus(status)
        })
        return () => {
            statusStream.remove(id)
        }
    })

    if (status === undefined) {
        return null
    }
    return <div className="gallery-status">
        {status.progress > 0 && (
            <div className="progress-container">
                <div
                    className="progress-bar"
                    style={{
                        width: `${status.progress}%`,
                    }}
                />
                <p className="progress-text">{status.progress}%</p>
            </div>
        )}
        {status.message && (
            <p className={`message ${status.error ? "message-error" : "message-success"}`} >
                {status.message}
            </p>
        )}
    </div>
}
